// Debounced Yjs field writes for useCollaborativeEncounter, extracted so the
// timing contract is unit-testable without a live provider.

// Tuning for updateFieldDebounced. maxWait guarantees a flush at least once
// per second during continuous typing, so a killed tab loses at most ~1s.
export const DEBOUNCED_WRITE_MS = 300;
export const DEBOUNCED_WRITE_MAX_WAIT_MS = 1000;

interface PendingWrite {
  /** The Yjs field the flush writes to */
  key: string;
  producer: () => any;
  timer: ReturnType<typeof setTimeout>;
  maxWaitTimer: ReturnType<typeof setTimeout>;
}

export interface DebouncedFieldWrites {
  /**
   * Debounce a field write. The producer runs at flush time and must read
   * live Yjs state, not a React snapshot. Returning undefined skips the
   * write. Writes with the same coalesceKey (default: key) replace each
   * other — latest wins.
   */
  updateFieldDebounced: (key: string, producer: () => any, coalesceKey?: string) => void;
  /** Flush all pending writes now, in insertion order. */
  flushPendingWrites: () => void;
}

export const createDebouncedFieldWrites = ({
  writeField,
  transact,
}: {
  writeField: (key: string, value: any) => void;
  /** Batches a multi-key flush so observers fire once (Yjs transaction) */
  transact: (fn: () => void) => void;
}): DebouncedFieldWrites => {
  // Pending writes keyed by coalesceKey
  const pending = new Map<string, PendingWrite>();

  const flushPendingWrites = () => {
    if (pending.size === 0) {
      return;
    }

    const entries = Array.from(pending.values());
    pending.clear();

    const doWrites = () => {
      // Insertion order. Dependent producers (e.g. the soapCustomEditWarning
      // producer reading its notes producer's noteWritten flag) must be
      // registered after their prerequisite.
      for (const entry of entries) {
        clearTimeout(entry.timer);
        clearTimeout(entry.maxWaitTimer);

        // A throwing producer must not abort the remaining flushes (or
        // propagate into sendOperation and fail a sign)
        try {
          const value = entry.producer();

          // undefined = producer declined the write
          if (value !== undefined) {
            writeField(entry.key, value);
          }
        } catch (e) {
          // Key + error name only — note content is PHI
          console.error('[collab] flush producer failed', entry.key, (e as Error)?.name);
        }
      }
    };

    if (entries.length > 1) {
      transact(doWrites);
    } else {
      doWrites();
    }
  };

  const updateFieldDebounced = (key: string, producer: () => any, coalesceKey?: string) => {
    const pendingKey = coalesceKey ?? key;

    // Timers drain ALL pending entries (flushPendingWrites), not just this
    // key. Paired producers (notes + its soapCustomEditWarning) must flush
    // together in insertion order — a per-key flush lets one entry's maxWait
    // consume it alone, silently dropping the partner's write.
    const existing = pending.get(pendingKey);

    if (existing) {
      // Replace producer, restart the trailing timer. maxWaitTimer keeps
      // running so continuous typing still flushes at least once a second.
      clearTimeout(existing.timer);
      existing.key = key;
      existing.producer = producer;
      existing.timer = setTimeout(flushPendingWrites, DEBOUNCED_WRITE_MS);
    } else {
      pending.set(pendingKey, {
        key,
        producer,
        timer: setTimeout(flushPendingWrites, DEBOUNCED_WRITE_MS),
        maxWaitTimer: setTimeout(flushPendingWrites, DEBOUNCED_WRITE_MAX_WAIT_MS),
      });
    }
  };

  return { updateFieldDebounced, flushPendingWrites };
};
